import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  initCmsBridge,
  isCmsEditEnabled,
  isCmsFramed,
  registerCmsLanguageSetter,
  reportChange,
  reportLocation,
  reportPages,
  subscribeCms,
  type CmsChange,
  type CmsPage,
} from "./cmsBridge";

interface CmsContextValue {
  editEnabled: boolean;
  language: string;
  reportChange: (change: CmsChange) => void;
}

const CmsContext = createContext<CmsContextValue>({
  editEnabled: false,
  language: "sv",
  reportChange: () => {},
});

export const useCms = (): CmsContextValue => useContext(CmsContext);

/** Title-case a route path into a readable label, e.g. "/menu/lunch" → "Lunch". */
function deriveLabel(path: string): string {
  if (path === "/" || path === "") return "Home";
  const last = path.replace(/\/+$/, "").split("/").filter(Boolean).pop() ?? "";
  return last
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Parse <loc> entries out of a sitemap.xml string into route paths. */
function parseSitemapPaths(xml: string): string[] {
  const paths = new Set<string>();
  for (const match of xml.matchAll(/<loc>(.*?)<\/loc>/g)) {
    try {
      paths.add(new URL(match[1].trim()).pathname || "/");
    } catch {
      const raw = match[1].trim();
      if (raw.startsWith("/")) paths.add(raw);
    }
  }
  return [...paths];
}

/**
 * Merge curated editable pages with sitemap-derived ones so Studio can list
 * EVERY page (and never miss one), while still knowing which are wired for
 * editing. Curated entries win on label/order and are flagged `editable: true`.
 */
function mergePages(curated: CmsPage[], sitemapPaths: string[]): CmsPage[] {
  const byPath = new Map<string, CmsPage>();
  for (const page of curated) {
    byPath.set(page.path, { ...page, editable: true });
  }
  for (const path of sitemapPaths) {
    if (!byPath.has(path)) {
      byPath.set(path, { path, label: deriveLabel(path), editable: false });
    }
  }
  return [...byPath.values()];
}

function parentOrigins(): string[] {
  const raw = import.meta.env.VITE_CMS_PARENT_ORIGINS as string | undefined;
  if (raw) {
    return raw
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
  }
  // Permissive in dev (unknown localhost ports); locked down in production.
  // The Studio admin is served from coretechs.se; admin.coretechs.se kept for
  // backwards compatibility. Override per-site with VITE_CMS_PARENT_ORIGINS.
  return import.meta.env.DEV
    ? ["*"]
    : ["https://coretechs.se", "https://www.coretechs.se", "https://admin.coretechs.se"];
}

interface CmsProviderProps {
  language: string;
  /** Apply a language change requested from Studio. */
  setLanguage?: (lang: string) => void;
  /** Routes Studio can offer in its page selector. */
  pages?: CmsPage[];
  children: React.ReactNode;
}

export const CmsProvider: React.FC<CmsProviderProps> = ({
  language,
  setLanguage,
  pages,
  children,
}) => {
  const [editEnabled, setEditEnabled] = useState(false);
  const [sitemapPaths, setSitemapPaths] = useState<string[]>([]);
  const location = useLocation();

  useEffect(() => {
    initCmsBridge({ allowedParentOrigins: parentOrigins() });
    setEditEnabled(isCmsEditEnabled());
    return subscribeCms(() => setEditEnabled(isCmsEditEnabled()));
  }, []);

  useEffect(() => {
    if (setLanguage) registerCmsLanguageSetter(setLanguage);
  }, [setLanguage]);

  // While framed, read the site's own sitemap.xml (same-origin, no CORS) so the
  // page list is complete even if EDITABLE_PAGES forgets a route.
  useEffect(() => {
    if (!isCmsFramed()) return;
    let cancelled = false;
    fetch("/sitemap.xml")
      .then((res) => (res.ok ? res.text() : ""))
      .then((xml) => {
        if (!cancelled && xml) setSitemapPaths(parseSitemapPaths(xml));
      })
      .catch(() => {
        /* no sitemap → fall back to curated pages only */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep Studio's page selector populated whenever we're framed (preview or
  // edit). reportPages no-ops when not framed, so the public site is unaffected.
  useEffect(() => {
    const merged = mergePages(pages ?? [], sitemapPaths);
    if (merged.length) reportPages(merged);
  }, [pages, sitemapPaths]);

  // Keep Studio's address/breadcrumb in sync with in-frame navigation.
  useEffect(() => {
    reportLocation(location.pathname + location.search);
  }, [location.pathname, location.search]);

  const value = useMemo<CmsContextValue>(
    () => ({ editEnabled, language, reportChange }),
    [editEnabled, language]
  );

  return <CmsContext.Provider value={value}>{children}</CmsContext.Provider>;
};
